// See services/sosInventoryService/README.md for information about the structure
// of schema.js files.
import { Decimal } from "classes/DecimalClasses";

import { i18n } from "services/i18nService";
import { formatDate } from "services/utility/dates";
import {
  extractName,
  formatDocumentInfo,
  extractLinkedTransactions,
  formatDateTimeToDate,
  formatNumberLinkField,
  formatPercent,
  formatBooleanToYesOrBlank,
  formatQuantityWithUom,
  extractDescription,
  getQuantityTotal,
  formatLinkableItem,
} from "services/utility/formatting";
import { formatLineDecimal } from "services/utility/list";
import { TYPES } from "services/utility/schema";
import { starColumn } from "services/utility/schema";

import { NO_COLUMNS } from "appConstants";
import { NO_REF_NUMBER_STRING, DEFAULT_DECIMALS_UNROUNDED } from "appConstants";

export const columnInfo = [
  starColumn,
  {
    name: "number",
    fixed: true,
    heading: i18n("columns.ReferenceNumber"),
    formatFunc: formatNumberLinkField,
    minWidth: "7em",
    linkField: true,
    sortable: true,
  },
  {
    name: "date",
    heading: i18n("columns.TxnDate"),
    formatFunc: formatDateTimeToDate,
    defaultSort: true,
    sortDirection: "asc",
    minWidth: "8em",
    sortable: true,
    default: true,
  },
  {
    name: "id",
    heading: i18n("columns.System"),
    sortDirection: "asc",
    minWidth: "7em",
    align: "right",
    sortable: true,
    default: true,
  },
  {
    name: "template",
    formatFunc: extractName,
    heading: i18n("columns.ProcessTemplate"),
    sortable: true,
    default: true,
  },
  {
    name: "location",
    heading: i18n("columns.Location"),
    formatFunc: extractName,
    sortable: true,
    default: true,
  },
  {
    name: "comment",
    heading: i18n("columns.Comment"),
    minWidth: "25em",
    maxWidth: "25em",
    sortable: true,
    default: true,
  },
  {
    name: "documents",
    heading: i18n("columns.Documents"),
    formatFunc: formatDocumentInfo,
    minWidth: "15em",
    default: true,
  },
  {
    name: "workCenter",
    fieldName: "workcenter",
    heading: i18n("columns.Workcenter"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "linkedTransaction",
    fieldName: "linkedWorkOrders",
    apiColumnName: "linkedWorkOrders",
    heading: i18n("columns.WorkOrder"),
    formatFunc: extractLinkedTransactions,
  },
  {
    name: "yield",
    heading: i18n("columns.Yield"),
    formatFunc: formatPercent,
    align: "right",
    sortable: true,
  },
  {
    name: "department",
    heading: i18n("columns.Department"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "startDate",
    heading: i18n("columns.StartDate"),
    formatFunc: formatDate,
  },
  {
    name: "hasSignature",
    heading: i18n("columns.Signature"),
    formatFunc: formatBooleanToYesOrBlank,
  },
];

export function getEmptyRecord(settings) {
  return {
    starred: 0,
    syncToken: 0,
    autoSerialLotNumbering: settings.autoSerialLotNumbers,
    number: "",
    date: new Date(),
    startDate: null,
    location: null,
    job: null,
    workcenter: null,
    department: null,
    linkedTransaction: null,
    linkedWorkOrders: [],
    comment: null,
    customFields: null,
    total: Decimal.ZERO,
    yield: Decimal.ZERO,
    archived: false,
    summaryOnly: false,
    hasSignature: false,
    template: null,
    multiplier: new Decimal(1),
    documents: [],
    keys: null,
    values: null,
    outputs: [],
    inputs: [],
  };
}

export const OUTPUTS_METADATA = {
  lineNumber: {},
  item: {
    type: TYPES.reference,
    initialValue: { id: null, name: null, description: null },
  },
  class: { type: TYPES.reference },
  jobWorkcenter: { type: TYPES.reference },
  tax: {
    type: TYPES.decimal,
    editStr: formatLineDecimal,
  },
  linkedTransaction: { type: TYPES.linkedTransaction },
  linkedTransactionRefNumber: { type: TYPES.text },
  linkedTransactionLineNumber: { type: TYPES.number },
  description: { type: TYPES.text },
  notes: { type: TYPES.text },
  quantity: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    initialValue: Decimal.ZERO,
  },
  weight: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  weightunit: { type: TYPES.text },
  volume: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  volumeunit: { type: TYPES.text },
  waste: { type: TYPES.boolean, initialValue: false },
  uom: { type: TYPES.reference },
  bin: { type: TYPES.reference },
  lot: { type: TYPES.reference },
  lotExpiration: { type: TYPES.date },
  serials: { type: TYPES.arrayOfReferences, initialValue: null },
  itemDetails: { initialValue: {} },
  relatedRecords: {
    initialValue: { item: {} },
  },
};

export const INPUTS_METADATA = {
  lineNumber: {},
  item: {
    type: TYPES.reference,
    initialValue: { id: null, name: null, description: null },
  },
  class: { type: TYPES.reference },
  jobWorkcenter: { type: TYPES.reference },
  tax: {
    type: TYPES.decimal,
    editStr: formatLineDecimal,
  },
  linkedTransaction: { type: TYPES.linkedTransaction },
  linkedTransactionRefNumber: { type: TYPES.text },
  linkedTransactionLineNumber: { type: TYPES.number },
  description: { type: TYPES.text },
  notes: { type: TYPES.text },
  quantity: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    initialValue: Decimal.ZERO,
  },
  weight: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  weightunit: { type: TYPES.text },
  worker: { type: TYPES.reference },
  volume: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  volumeunit: { type: TYPES.text },
  waste: { type: TYPES.boolean, initialValue: false },
  uom: { type: TYPES.reference },
  bin: { type: TYPES.reference },
  lot: { type: TYPES.text },
  lotExpiration: { type: TYPES.date },
  serials: { type: TYPES.arrayOfReferences, initialValue: null },
  onhand: {
    type: TYPES.itemField,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  itemDetails: { initialValue: {} },
  relatedRecords: {
    initialValue: { item: {} },
  },
};

export const LINE_ITEM_METADATA = {
  //TODO: Update LineComponents to handle Input/Output editStr
  quantity: { editStr: formatLineDecimal },
  linkedTransaction: { transactionType: "WO" },
};

export const FIELDS_TO_TRANSFORM = {
  DECIMAL_FIELDS: ["yield", "total"],
  MONEY_FIELDS: [],
  DATE_FIELDS: ["startDate"],
  DATETIME_FIELDS: ["date"],
  NON_UTC_DATETIME_FIELDS: [],
};

export const REFERENCE_FIELDS = ["location"];

export const LINE_ACTIONS = [
  ["quickView", "pdf", "email"],
  "template",
  "archive",
  "delete",
  "history",
  "sync",
];
export const BATCH_ACTIONS = [["pdf", "email"], "delete", "archive", "sync"];

export const OUTPUT_ITEM_CALCULATED_FIELDS = [NO_COLUMNS];
export const INPUT_ITEM_CALCULATED_FIELDS = ["locationBins"];

export const QUICK_VIEW_CONFIG = {
  signatureLinesLabel: i18n("colon.ReceivedBy"),
  tableConfig: [
    { type: "outputs", label: i18n("global.Outputs") },
    { type: "inputs", label: i18n("global.Inputs") },
  ],
  headerFields: [
    {
      name: "date",
      formatFunc: formatDateTimeToDate,
      label: i18n("columns.Date"),
    },
    {
      name: "number",
      label: i18n("frmLabel.ProcessNumber"),
      defaultValue: NO_REF_NUMBER_STRING,
    },
  ],
  fields: [
    {
      label: i18n("frmLabel.Location"),
      name: "location",
      formatFunc: extractName,
    },
  ],
  lines: [
    {
      name: "item",
      formatFunc: formatLinkableItem,
      heading: i18n("columns.Item"),
      align: "left",
      isItemField: true,
    },
    {
      name: "description",
      formatFunc: extractDescription,
      heading: i18n("columns.Description"),
      align: "left",
    },
    {
      name: "quantity",
      formatFunc: formatQuantityWithUom,
      footerFunc: getQuantityTotal,
      heading: i18n("columns.Quantity"),
      align: "right",
    },
  ],
};
